import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  DataGridPremium,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { LicenseInfo } from "@mui/x-license";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import { useGetXranCertsQuery } from "api/homeApi";
import ContentUnavailable from "components/ContentUnavailable";

LicenseInfo.setLicenseKey(
  "7b2a7be60c5eff8e15f1f2ae43e1260dTz0xMjMyNTksRT0xNzk3NDY1NTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y"
);

// ─── Styled components ────────────────────────────────────────────────────────

const PageContainer = styled(Box)({
  backgroundColor: "#F5F5F5",
  minHeight: "100vh",
  padding: "2rem",
});

const ContentCard = styled(Box)({
  backgroundColor: "#FFFFFF",
  borderRadius: "12px",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  padding: "1.5rem",
});

const PageTitle = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "1.75rem",
  color: "#1565C0",
  textAlign: "center",
  marginBottom: "1.5rem",
});

const FilterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem",
  alignItems: "flex-end",
  backgroundColor: "#F8F9FA",
  border: "1px solid #E0E0E0",
  borderRadius: "8px",
  padding: "1rem",
  marginBottom: "1.25rem",
}));

const StyledDataGridPremium = styled(DataGridPremium)({
  "& .MuiDataGrid-iconButtonContainer": {
    visibility: "visible",
    width: "auto",
  },
  "& .MuiDataGrid-sortIcon": {
    opacity: "inherit !important",
  },
  "& .MuiDataGrid-cell": {
    paddingTop: "5px !important",
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#1565C0",
    color: "#fff !important",
  },
  "& .MuiDataGrid-sortIcon, .MuiDataGrid-filterIcon": {
    color: "#fff !important",
  },
  "& .MuiDataGrid-columnHeader:hover": {
    color: "#fff !important",
  },
  "& .MuiDataGrid-filterIcon": {
    color: "#fff !important",
  },
  "& .MuiDataGrid-toolbarContainer .MuiButtonBase-root": {
    color: "#005AA6",
  },
  "& .MuiDataGrid-menuIconButton": {
    color: "#fff",
  },
  "& .row-expired": {
    backgroundColor: "#FFEBEE !important",
    "&:hover": { backgroundColor: "#FFCDD2 !important" },
  },
  "& .row-expiring-soon": {
    backgroundColor: "#FFF8E1 !important",
    "&:hover": { backgroundColor: "#FFECB3 !important" },
  },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const yr = d.getFullYear();
  return `${m}/${day}/${yr}`;
};

const getExpiryStatus = (dateString) => {
  if (!dateString) return "ok";
  const expiry = new Date(dateString);
  const now = new Date();
  if (expiry < now) return "expired";
  const daysUntil = (expiry - now) / (1000 * 60 * 60 * 24);
  if (daysUntil <= 30) return "expiring-soon";
  return "ok";
};

// Simple debounce hook
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Component ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 100;

export default function XranCerts() {
  const apiRef = useGridApiRef();

  // ── Filter state ────────────────────────────────────────────────────────────
  const [hostInput, setHostInput] = useState("");
  const [certNameInput, setCertNameInput] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [certTypeFilter, setCertTypeFilter] = useState("");
  const [issuedAfter, setIssuedAfter] = useState(null);   // dayjs or null
  const [expiresBefore, setExpiresBefore] = useState(null); // dayjs or null
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: PAGE_SIZE });

  const debouncedHost = useDebounce(hostInput, 400);
  const debouncedCertName = useDebounce(certNameInput, 400);

  // Reset to first page whenever any filter changes
  const prevFiltersRef = useRef({});
  useEffect(() => {
    const key = JSON.stringify({ debouncedHost, debouncedCertName, siteFilter, certTypeFilter, issuedAfter, expiresBefore });
    if (prevFiltersRef.current.key !== key) {
      prevFiltersRef.current.key = key;
      setPaginationModel((m) => (m.page === 0 ? m : { ...m, page: 0 }));
    }
  }, [debouncedHost, debouncedCertName, siteFilter, certTypeFilter, issuedAfter, expiresBefore]);

  // ── Build query params ───────────────────────────────────────────────────────
  const queryParams = useMemo(() => {
    const p = {
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    };
    if (siteFilter) p.site = siteFilter;
    if (debouncedHost) p.host = debouncedHost;
    if (certTypeFilter) p.cert_type = certTypeFilter;
    if (debouncedCertName) p.cert_name = debouncedCertName;
    if (issuedAfter) {
      // We repurpose expiring_in_days not applicable for issue date.
      // IssueDate "after" is a client-side filter applied on returned rows for now,
      // since the backend doesn't have an issued_after param.
      // (backend can be extended later)
    }
    if (expiresBefore) {
      // ExpiryDate "before" maps to the backend expiring_in_days only for future dates.
      // For a precise before-date filter we send it as a separate param.
      // Since the backend doesn't have an expire_before param yet, we'll compute
      // expiring_in_days as days from now to expiresBefore (only if expiresBefore > now).
      const now = dayjs();
      const diff = expiresBefore.diff(now, "day");
      if (diff >= 0) {
        p.expiring_in_days = diff;
      } else {
        // expiresBefore is in the past — show only expired records up to that date
        p.expired_only = true;
      }
    }
    return p;
  }, [paginationModel, siteFilter, debouncedHost, certTypeFilter, debouncedCertName, expiresBefore, issuedAfter]);

  const { data: pageData, isLoading, isFetching, isError } = useGetXranCertsQuery(queryParams);

  // ── Initial full fetch for site / certType dropdowns ────────────────────────
  const { data: allData } = useGetXranCertsQuery({ limit: 2000 });

  const siteOptions = useMemo(() => {
    if (!allData?.results) return [];
    return [...new Set(allData.results.map((r) => r.Site).filter(Boolean))].sort();
  }, [allData]);

  const certTypeOptions = useMemo(() => {
    if (!allData?.results) return [];
    return [...new Set(allData.results.map((r) => r.CertType).filter(Boolean))].sort();
  }, [allData]);

  // ── Build grid rows (with optional client-side issued-after filter) ───────────
  const rows = useMemo(() => {
    if (!pageData?.results) return [];
    let results = pageData.results;
    if (issuedAfter) {
      const threshold = issuedAfter.toDate();
      results = results.filter((r) => r.IssueDate && new Date(r.IssueDate) >= threshold);
    }
    return results.map((r) => ({
      id: r.ID,
      site: r.Site,
      host: r.Host,
      certName: r.CertName,
      certType: r.CertType,
      issueDate: r.IssueDate,
      expiryDate: r.ExpiryDate,
    }));
  }, [pageData, issuedAfter]);

  const totalCount = pageData?.total ?? 0;

  // ── Column definitions ───────────────────────────────────────────────────────
  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
      type: "number",
    },
    {
      field: "site",
      headerName: "Site",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "host",
      headerName: "Host",
      flex: 1.2,
      minWidth: 150,
    },
    {
      field: "certName",
      headerName: "Cert Name",
      flex: 1.8,
      minWidth: 200,
    },
    {
      field: "certType",
      headerName: "Cert Type",
      flex: 0.9,
      minWidth: 120,
    },
    {
      field: "issueDate",
      headerName: "Issue Date",
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.875rem" }}>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => {
        const status = getExpiryStatus(params.value);
        const color =
          status === "expired"
            ? "#C62828"
            : status === "expiring-soon"
            ? "#E65100"
            : "#2E7D32";
        return (
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              fontWeight: status !== "ok" ? 600 : 400,
              color,
            }}
          >
            {formatDate(params.value)}
          </Typography>
        );
      },
    },
  ];

  // ── Row class for background color ──────────────────────────────────────────
  const getRowClassName = useCallback((params) => {
    const status = getExpiryStatus(params.row.expiryDate);
    if (status === "expired") return "row-expired";
    if (status === "expiring-soon") return "row-expiring-soon";
    return "";
  }, []);

  // ── Clear filters ─────────────────────────────────────────────────────────────
  const handleClearFilters = () => {
    setHostInput("");
    setCertNameInput("");
    setSiteFilter("");
    setCertTypeFilter("");
    setIssuedAfter(null);
    setExpiresBefore(null);
  };

  const hasActiveFilters =
    hostInput || certNameInput || siteFilter || certTypeFilter || issuedAfter || expiresBefore;

  // ── Legend ────────────────────────────────────────────────────────────────────
  const legend = (
    <Box sx={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "center" }}>
      {[
        { color: "#FFEBEE", border: "#EF9A9A", label: "Expired" },
        { color: "#FFF8E1", border: "#FFD54F", label: "Expiring within 30 days" },
      ].map(({ color, border, label }) => (
        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "3px",
              backgroundColor: color,
              border: `1px solid ${border}`,
            }}
          />
          <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.75rem", color: "#555" }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // ── Loading / error states ────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <PageContainer>
        <ContentCard sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </ContentCard>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <ContentCard>
          <Alert severity="error">Failed to load xRAN certificate data. Please try again later.</Alert>
        </ContentCard>
      </PageContainer>
    );
  }

  if (!isLoading && totalCount === 0 && !hasActiveFilters) {
    return (
      <PageContainer>
        <ContentCard>
          <ContentUnavailable page="xRAN Certificates" />
        </ContentCard>
      </PageContainer>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <PageContainer>
        <ContentCard>
          <PageTitle>xRAN Certificates</PageTitle>

          {/* Filter Bar */}
          <FilterBar>
            {/* Site dropdown */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ fontFamily: "Montserrat" }}>Site</InputLabel>
              <Select
                value={siteFilter}
                label="Site"
                onChange={(e) => setSiteFilter(e.target.value)}
                sx={{ fontFamily: "Montserrat" }}
              >
                <MenuItem value="">
                  <em>All Sites</em>
                </MenuItem>
                {siteOptions.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontFamily: "Montserrat" }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Host text input */}
            <TextField
              label="Host"
              size="small"
              value={hostInput}
              onChange={(e) => setHostInput(e.target.value)}
              placeholder="Type to search…"
              sx={{ minWidth: 200, "& *": { fontFamily: "Montserrat" } }}
            />

            {/* Cert Type dropdown */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ fontFamily: "Montserrat" }}>Cert Type</InputLabel>
              <Select
                value={certTypeFilter}
                label="Cert Type"
                onChange={(e) => setCertTypeFilter(e.target.value)}
                sx={{ fontFamily: "Montserrat" }}
              >
                <MenuItem value="">
                  <em>All Types</em>
                </MenuItem>
                {certTypeOptions.map((ct) => (
                  <MenuItem key={ct} value={ct} sx={{ fontFamily: "Montserrat" }}>
                    {ct}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Cert Name text input */}
            <TextField
              label="Cert Name"
              size="small"
              value={certNameInput}
              onChange={(e) => setCertNameInput(e.target.value)}
              placeholder="Type to search…"
              sx={{ minWidth: 200, "& *": { fontFamily: "Montserrat" } }}
            />

            {/* Issue Date — after */}
            <DatePicker
              label="Issued After"
              value={issuedAfter}
              onChange={(v) => setIssuedAfter(v)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 160, "& *": { fontFamily: "Montserrat" } },
                },
                actionBar: { actions: ["clear"] },
              }}
            />

            {/* Expiry Date — before */}
            <DatePicker
              label="Expires Before"
              value={expiresBefore}
              onChange={(v) => setExpiresBefore(v)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 160, "& *": { fontFamily: "Montserrat" } },
                },
                actionBar: { actions: ["clear"] },
              }}
            />

            {/* Clear filters button */}
            {hasActiveFilters && (
              <Tooltip title="Clear all filters">
                <IconButton
                  onClick={handleClearFilters}
                  size="small"
                  sx={{ color: "#C62828", alignSelf: "center" }}
                >
                  <FilterAltOffIcon />
                </IconButton>
              </Tooltip>
            )}
          </FilterBar>

          {/* No-results message when filters are active */}
          {totalCount === 0 && hasActiveFilters && (
            <Alert severity="info" sx={{ mb: 2, fontFamily: "Montserrat" }}>
              No certificates match the current filters.
            </Alert>
          )}

          {/* Color legend + export row */}
          <Box sx={{ display: "flex", alignItems: "center", mb: "1rem" }}>
            {legend}
            <Box sx={{ flexGrow: 1 }} />
            {totalCount > 0 && (
              <Chip
                label={`${totalCount.toLocaleString()} total`}
                size="small"
                sx={{ mr: 1.5, fontFamily: "Montserrat", backgroundColor: "#E3F2FD", color: "#1565C0" }}
              />
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() =>
                apiRef.current.exportDataAsExcel({
                  fileName: "xran-certs-export",
                  allColumns: true,
                })
              }
              sx={{ fontFamily: "Montserrat", textTransform: "none" }}
            >
              Export to Excel
            </Button>
          </Box>

          {/* Data Grid */}
          <StyledDataGridPremium
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            rowCount={totalCount}
            loading={isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[50, 100, 200]}
            getRowClassName={getRowClassName}
            disableRowSelectionOnClick
            density="compact"
            autoHeight
            sx={{ fontFamily: "Montserrat" }}
          />
        </ContentCard>
      </PageContainer>
    </LocalizationProvider>
  );
}
